/**
 * Every change to a board goes through here.
 *
 * These are the only writes. Keeping them in one place is what lets the sync layer,
 * the persistence layer and per-user undo all watch a single, well-defined set of
 * operations rather than guessing at arbitrary edits made all over the app.
 *
 * Ink is created locally first and only then handed to this file
 * (docs/03-ARCHITECTURE.md §7.2) — nothing here waits for anything.
 */
import { newObjectId, newStrokeId, } from '@opal/shared';
import { buildPage, clonePage, findPage, fromStoredStroke, getBackground, getObjects, getPages, getStrokes, readObject, toStoredStroke, writeObject, PAGE_BACKGROUND_KEY, } from './schema.js';
/** Fields a later edit may change. `id` and `createdAt` are fixed at creation. */
const EDITABLE_OBJECT_FIELDS = [
    'kind',
    'createdByModule',
    'userId',
    'x',
    'y',
    'width',
    'height',
    'rotation',
    'zIndex',
    'layer',
    'locked',
    'data',
];
/**
 * Groups several changes so they travel as one update and undo as one step.
 *
 * `origin` marks who made the change: the sync layer uses it to avoid echoing a
 * remote edit straight back out again.
 */
export function transact(doc, fn, origin) {
    doc.transact(fn, origin);
}
function requirePage(doc, pageId) {
    const page = findPage(doc, pageId);
    if (!page)
        throw new Error(`Board has no page ${pageId}`);
    return page;
}
/** Position of an item with this id, or -1. */
function indexOfStroke(strokes, strokeId) {
    return strokes.toArray().findIndex((stroke) => stroke.id === strokeId);
}
function indexOfObject(objects, objectId) {
    return objects.toArray().findIndex((object) => object.get('id') === objectId);
}
// ---------------------------------------------------------------------------
// Strokes
// ---------------------------------------------------------------------------
/** Puts a finished stroke on the page. Returns its id. */
export function addStroke(doc, pageId, input, origin) {
    const stroke = { ...input, id: newStrokeId(), createdAt: Date.now() };
    const page = requirePage(doc, pageId);
    transact(doc, () => { getStrokes(page).push([toStoredStroke(stroke)]); }, origin);
    return stroke.id;
}
/**
 * Puts a stroke that already has an id on the page.
 *
 * Ink is drawn locally before anything is shared, so it already carries the id it was
 * drawn with; keeping that id is what lets the same stroke be erased later on any
 * device. Putting the same stroke twice does nothing.
 */
export function putStroke(doc, pageId, stroke, origin) {
    const strokes = getStrokes(requirePage(doc, pageId));
    if (indexOfStroke(strokes, stroke.id) >= 0)
        return;
    transact(doc, () => {
        strokes.push([toStoredStroke(stroke)]);
    }, origin);
}
/**
 * Swaps a stroke for a changed version of itself, in the place it already occupies.
 *
 * A stroke is frozen once drawn (docs/05-DATA-MODEL.md §3.1), so moving, resizing or
 * recolouring one means replacing the record. Keeping its id and its position in the
 * page is what keeps the ink in the order it was drawn, and what lets everyone else's
 * screen recognise it as the same stroke rather than as new work.
 *
 * False when the page no longer holds a stroke with that id.
 */
export function replaceStroke(doc, pageId, stroke, origin) {
    const strokes = getStrokes(requirePage(doc, pageId));
    const index = indexOfStroke(strokes, stroke.id);
    if (index < 0)
        return false;
    transact(doc, () => {
        strokes.delete(index, 1);
        strokes.insert(index, [toStoredStroke(stroke)]);
    }, origin);
    return true;
}
/** Erases one stroke. Two people erasing the same one at once is harmless. */
export function removeStroke(doc, pageId, strokeId, origin) {
    const strokes = getStrokes(requirePage(doc, pageId));
    const index = indexOfStroke(strokes, strokeId);
    if (index < 0)
        return false;
    transact(doc, () => { strokes.delete(index, 1); }, origin);
    return true;
}
/** Every stroke on the page, oldest first, with `points` back as a Float32Array. */
export function listStrokes(doc, pageId) {
    return getStrokes(requirePage(doc, pageId)).toArray().map(fromStoredStroke);
}
export function findStroke(doc, pageId, strokeId) {
    const stored = getStrokes(requirePage(doc, pageId))
        .toArray()
        .find((stroke) => stroke.id === strokeId);
    return stored ? fromStoredStroke(stored) : null;
}
// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------
/** Places a shape, text box, badge or any other object. Returns its id. */
export function addObject(doc, pageId, input, origin) {
    const now = Date.now();
    const object = { ...input, id: newObjectId(), createdAt: now, updatedAt: now };
    const page = requirePage(doc, pageId);
    transact(doc, () => { getObjects(page).push([writeObject(object)]); }, origin);
    return object.id;
}
/**
 * Puts an object that already has an id on the page.
 *
 * A shape is drawn on the board before anything is shared, exactly like ink
 * (docs/03-ARCHITECTURE.md §7.2), so it already carries the id it was drawn with.
 * Keeping that id is what lets the same shape be moved or rubbed out later on any
 * device. Putting the same object twice does nothing.
 */
export function putObject(doc, pageId, object, origin) {
    const objects = getObjects(requirePage(doc, pageId));
    if (indexOfObject(objects, object.id) >= 0)
        return;
    transact(doc, () => { objects.push([writeObject(object)]); }, origin);
}
/**
 * Changes some fields of an object and leaves the rest alone.
 *
 * Only the named fields are written, so a person moving an object and a person
 * recolouring it at the same moment both keep their change.
 */
export function updateObject(doc, pageId, objectId, patch, origin) {
    const objects = getObjects(requirePage(doc, pageId));
    const index = indexOfObject(objects, objectId);
    if (index < 0)
        return false;
    const map = objects.get(index);
    transact(doc, () => {
        for (const field of EDITABLE_OBJECT_FIELDS) {
            const value = patch[field];
            if (value !== undefined)
                map.set(field, value);
        }
        map.set('updatedAt', Date.now());
    }, origin);
    return true;
}
export function removeObject(doc, pageId, objectId, origin) {
    const objects = getObjects(requirePage(doc, pageId));
    const index = indexOfObject(objects, objectId);
    if (index < 0)
        return false;
    transact(doc, () => { objects.delete(index, 1); }, origin);
    return true;
}
export function listObjects(doc, pageId) {
    return getObjects(requirePage(doc, pageId)).toArray().map(readObject);
}
export function findObject(doc, pageId, objectId) {
    const objects = getObjects(requirePage(doc, pageId));
    const index = indexOfObject(objects, objectId);
    return index < 0 ? null : readObject(objects.get(index));
}
// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------
/** Adds a page at the end. Returns its id. */
export function addPage(doc, name, origin) {
    const pages = getPages(doc);
    const order = pages.length;
    const page = buildPage(name ?? `Page ${order + 1}`, order);
    transact(doc, () => { pages.push([page]); }, origin);
    return page.get('id');
}
/**
 * Renames a page (SRS C1). An empty name is ignored: a tab with no label cannot be
 * found again, and the teacher can always type over the old one instead.
 */
export function renamePage(doc, pageId, name, origin) {
    const trimmed = name.trim();
    if (trimmed === '')
        return;
    const page = requirePage(doc, pageId);
    transact(doc, () => { page.set('name', trimmed); }, origin);
}
/** Where a page sits in the tab strip, or -1 when the board has no such page. */
function indexOfPage(doc, pageId) {
    return getPages(doc)
        .toArray()
        .findIndex((page) => page.get('id') === pageId);
}
/** Renumbers every page from the top, so `order` always matches the tab strip. */
function renumber(pages) {
    pages.toArray().forEach((page, index) => {
        page.set('order', index);
    });
}
/**
 * Moves a page to another place in the strip (SRS C1 — drag to reorder).
 *
 * The page itself is taken out and put back rather than copied, so everything drawn
 * on it travels with it and nobody else's view of it is disturbed.
 */
export function movePage(doc, pageId, toIndex, origin) {
    const pages = getPages(doc);
    const from = indexOfPage(doc, pageId);
    if (from < 0)
        return false;
    const to = Math.min(Math.max(Math.trunc(toIndex), 0), pages.length - 1);
    if (to === from)
        return false;
    transact(doc, () => {
        const page = pages.get(from);
        // Yjs holds a type in one place only, so the page is rebuilt where it lands.
        const copy = clonePage(page, page.get('name'));
        pages.delete(from, 1);
        pages.insert(to, [copy]);
        renumber(pages);
    }, origin);
    return true;
}
/**
 * A second page holding everything the first one holds (SRS C1 — duplicate).
 *
 * Every stroke and every object is copied with a NEW id: two pages must never share
 * one, or erasing a line on the copy would take it off the original too.
 */
export function duplicatePage(doc, pageId, origin) {
    const pages = getPages(doc);
    const from = indexOfPage(doc, pageId);
    if (from < 0)
        return null;
    const source = pages.get(from);
    const copy = clonePage(source, `${source.get('name')} copy`, true);
    transact(doc, () => {
        pages.insert(from + 1, [copy]);
        renumber(pages);
    }, origin);
    return copy.get('id');
}
/**
 * Takes a page off the board (SRS C1). Returns false when it is the last one: a board
 * with no pages has nothing to draw on, so the last page can only be emptied.
 */
export function removePage(doc, pageId, origin) {
    const pages = getPages(doc);
    if (pages.length <= 1)
        return false;
    const index = indexOfPage(doc, pageId);
    if (index < 0)
        return false;
    transact(doc, () => {
        pages.delete(index, 1);
        renumber(pages);
    }, origin);
    return true;
}
/** Changes the paper a page is drawn on (SRS C7). Unnamed fields keep their value. */
export function setPageBackground(doc, pageId, patch, origin) {
    const page = requirePage(doc, pageId);
    const background = page.get(PAGE_BACKGROUND_KEY);
    const current = getBackground(page);
    transact(doc, () => {
        background.set('type', patch.type ?? current.type);
        background.set('gridSize', patch.gridSize ?? current.gridSize);
        background.set('color', patch.color ?? current.color);
    }, origin);
}
//# sourceMappingURL=mutations.js.map